import { ActorObject, ActorType } from '../../shared/datamodel';
import { AppObject } from '../../application/app.datamodel';
import { ComponentObject, ComponentType } from '../../publish/component/store/component.datamodel';
import { ConfigurationObject } from '../../configuration/store/configuration.datamodel';

export class SupportResultObject {
  type: SupportSearchType;
  name: string;
  data: any;

  /**
   * Constructor.
   * @param type the type of the result
   * @param name the name of the result
   * @param data the data of the result
   */
  constructor(type: SupportSearchType, name: string, data: any) {
    this.type = type;
    this.name = name;
    this.data = data;
  }

  /**
   * Equality test of SupportResultObject.
   * @param object
   * @returns {boolean}
   */
  public equals(object: SupportResultObject): boolean {
    if (this === object) {
      return true;
    } else {
      if (object === undefined) {
        return false;
      } else {
        return this.type === object.type && this.name === object.name;
      }
    }
  }
}

export enum SupportSearchType {
  OFFICE = "OFFICE" as any,
  USER = "USER" as any,
  CMP = "CMP" as any,
  PRODUCT = "PRODUCT" as any
}

export const SEARCH_REGEX = /^[-&(),./\w\s]*$/;
/**
 * Utility class for SupportSearchType.
 */
export class SupportSearchTypeUtils {

  /**
   * Convert SupportSearchType to ActorType.
   * @param searchType
   */
  static toActorType(searchType: SupportSearchType) {
    switch (searchType) {
      case SupportSearchType.CMP:
        return ActorType.COMPONENT;
      case SupportSearchType.OFFICE:
        return ActorType.OFFICE;
      case SupportSearchType.PRODUCT:
        return ActorType.PRODUCT;
      case SupportSearchType.USER:
        return ActorType.USER_ORGANISATION;
    }
    return undefined;
  }
}

export enum SupportStatusLabel {
  STATUS_ACTIVATED = "activated" as any,
  STATUS_DEACTIVATED = "deactivated" as any,
  STATUS_VISIBLE = "visible" as any,
  STATUS_HIDDEN = "hidden" as any
}

export enum SupportComponentType {
  SF = "SF" as any,
  ST = "ST" as any,
  FF = "FF" as any,
  QM = "QM" as any,
  PS = "PS" as any
}

export interface TroubleshootingSearch {
  office?: string;
  user?: string;
  org?: string;
  ogu?: string;
  product?: string;
}

export type TroubleshootingSearchType = 'user'|'office'|'ogu'|'org'|'product';

export interface TroubleshootingSearchWithType extends TroubleshootingSearch {
  entityType: TroubleshootingSearchType;
}

export const TroubleshootingSearchInputs: {[type: string]: EntityInput} = {
  product: {
    type: 'product',
    label: 'Product',
    defaultValue: 'Selling Platform Connect'
  },
  org: {
    type: 'org',
    label: 'Organization',
  },
  ogu: {
    type: 'ogu',
    label: 'Organization Unit',
  },
  office: {
    type: 'office',
    label: 'Office',
  },
  user: {
    type: 'user',
    label: 'Username',
  }
};

export interface EntityInput {
  type: TroubleshootingSearchType;
  label: string;
  defaultValue?: string;
}

export interface TroubleshootingResultKey {
  actor: ActorObject;
  officeId?: string;
}

export interface TroubleshootingResult {
  key: TroubleshootingResultKey;
  searchItem: TroubleshootingSearchWithType;
  result: SingleTroubleshootingResult;
  orderedActors: ActorObject[];
}

export interface SingleTroubleshootingResult {
  [appId: string]: TroubleshootingAppInfo
}

export interface TroubleshootingAppInfo {
  access: ActorToApplications[];
  application: {app: Partial<AppObject>, components: ComponentObject[]};
  configurations: {[key: string]: ConfigurationObject[]};
}

export interface ActorToApplications {
  actor: ActorObject,
  applications: CatalogComponent[]
}

export interface DeployedActorToApplication {
  actor: ActorObject,
  application: CatalogComponent & {excludedBy?: ActorObject}
}

export interface ParameterMergedValueSources {
  parameterValue: string;
  sources: ParameterMergedSource[];
}

export interface ParameterMergedResult {
  conflicts: Map<string, Set<ParameterMergedSource>>,
  overrides: Map<string, ParameterMergedValueSources>
}

export interface ParameterMergedSource {
  appId: string;
  componentId: string;
}

export interface CatalogComponent {
  guid: string,
  version: number,
  isActive: boolean,
  isExcluded: boolean,
  isConfigurationOverride: boolean,
  isVisible: boolean,
  updatedOn: number,
  configurationGuid?: string,
  configurationVersion?: string
  configurations?: {[key: string]: string}
}

export interface TroubleshootingTableRowData {
  appType: string,
  appId: string,
  appName: string,
  componentType: ComponentType,
  componentName: string,
  componentId: string,
  actorType: string,
  actorName: string,
  status: string,
  comment: string,
  deploymentTimestamp: string,
  configurationId?: string,
  configurationName?: string,
  configurationContent?: string,
  url?: string,
  launchType?: string,
  modality?: string,
  httpsMethod?: string,
  parameterName?: string,
  parameterValue?: string,
  componentContent?: string
}

export enum TroubleshootingComponentFamily {
  SCRIPT = 'script' as any,
  PARAMETERS = 'parameters' as any,
}
export const ERROR_MESSAGE = 'Oops! An error has been occurred during searching. ' +
  'Please try again or contact your helpdesk if the problem still occurs.';

export const NEW_TAB_MESSAGE = 'Showing search results in a new tab';

export const ALREADY_OPEN_MESSAGE = 'This entity is already displayed in a search results tab';

